import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { supabase } from '../config/supabase';
import i18n from '../i18n';
import { getMuiLocaleExtension } from '../utils/muiLocale';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

// Cores da GIO - Versão Suavizada
const gioColors = {
  background: '#0a0b1e',
  darkPurple: '#1e1b3a',
  primary: '#5b59d6',
  secondary: '#6b6fd8',
  accent: '#a8c904',
  white: '#ffffff',
  lightGray: '#f8fafc',
  darkGray: '#1a202c',
  mediumGray: '#4a5568',
  // Versões mais suaves para elementos menos importantes
  primarySoft: '#8b89e3',
  secondarySoft: '#9b9fe5',
  purpleSoft: '#3a3654'
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme deve ser usado dentro de um ThemeProvider');
  }
  return context;
};

// Opções base do tema (locale MUI é aplicado em createTheme(base, locale))
const lightThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: gioColors.primary,
      light: gioColors.secondary,
      dark: gioColors.darkPurple,
    },
    secondary: {
      main: gioColors.secondary,
      light: gioColors.accent,
      dark: gioColors.primary,
    },
    background: {
      default: gioColors.lightGray,
      paper: gioColors.white,
    },
    text: {
      primary: gioColors.darkGray,
      secondary: gioColors.mediumGray,
    },
    success: {
      main: gioColors.accent,
    },
  },
  spacing: 8,
  typography: {
    fontFamily: '"Inter", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: `0 1px 3px 0 rgba(105, 103, 251, 0.1), 0 1px 2px 0 rgba(105, 103, 251, 0.06)`,
          borderRadius: '16px',
          border: `1px solid rgba(105, 103, 251, 0.1)`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          background: `linear-gradient(135deg, ${gioColors.primarySoft} 0%, ${gioColors.secondarySoft} 100%)`,
          boxShadow: `0 4px 12px rgba(91, 89, 214, 0.2)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${gioColors.primary} 0%, ${gioColors.secondary} 100%)`,
            boxShadow: `0 6px 15px rgba(91, 89, 214, 0.3)`,
          },
        },
      },
    },
  },
};

const darkThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: gioColors.primary,
      light: gioColors.secondary,
      dark: gioColors.darkPurple,
    },
    secondary: {
      main: gioColors.secondary,
      light: gioColors.accent,
      dark: gioColors.primary,
    },
    background: {
      default: gioColors.background,
      paper: gioColors.darkPurple,
    },
    text: {
      primary: gioColors.white,
      secondary: 'rgba(255, 255, 255, 0.8)',
    },
    success: {
      main: gioColors.accent,
    },
    divider: `rgba(105, 103, 251, 0.2)`,
  },
  spacing: 8,
  typography: {
    fontFamily: '"Inter", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  },
    components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${gioColors.darkPurple} 0%, rgba(91, 89, 214, 0.08) 100%)`,
          boxShadow: `0 4px 6px -1px rgba(91, 89, 214, 0.2), 0 2px 4px -1px rgba(91, 89, 214, 0.15)`,
          borderRadius: '16px',
          border: `1px solid rgba(91, 89, 214, 0.2)`,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          textTransform: 'none',
          fontWeight: 600,
        },
        contained: {
          background: `linear-gradient(135deg, ${gioColors.primarySoft} 0%, ${gioColors.secondarySoft} 100%)`,
          boxShadow: `0 4px 12px rgba(91, 89, 214, 0.25)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${gioColors.primary} 0%, ${gioColors.secondary} 100%)`,
            boxShadow: `0 6px 15px rgba(91, 89, 214, 0.35)`,
          },
        },
        outlined: {
          borderColor: gioColors.primarySoft,
          color: gioColors.primarySoft,
          '&:hover': {
            borderColor: gioColors.primary,
            backgroundColor: `rgba(91, 89, 214, 0.08)`,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: `linear-gradient(135deg, ${gioColors.background} 0%, ${gioColors.purpleSoft} 100%)`,
          boxShadow: `0 4px 20px rgba(91, 89, 214, 0.2)`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // Sólido: o gradiente com alpha ~0.04 deixava Paper/Autocomplete translúcidos.
          backgroundImage: 'none',
          backgroundColor: gioColors.darkPurple,
          border: `1px solid rgba(91, 89, 214, 0.15)`,
        },
      },
    },
    // Menus/popovers/autocomplete usam Paper — garantir fundo opaco sobre o conteúdo.
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: gioColors.darkPurple,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: gioColors.darkPurple,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: gioColors.darkPurple,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: gioColors.darkPurple,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          background: `rgba(91, 89, 214, 0.15)`,
          color: gioColors.white,
          border: `1px solid rgba(91, 89, 214, 0.2)`,
        },
      },
    },
  },
};

export const ThemeProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [themeMode, setThemeMode] = useState('light');
  const [loading, setLoading] = useState(true);
  const [appLanguage, setAppLanguage] = useState(() => i18n.language || 'pt-BR');

  useEffect(() => {
    const onLang = (lng) => setAppLanguage(lng || 'pt-BR');
    i18n.on('languageChanged', onLang);
    setAppLanguage(i18n.language || 'pt-BR');
    return () => i18n.off('languageChanged', onLang);
  }, []);

  // Tema a partir do user do AuthContext — sem getUser() duplicado
  useEffect(() => {
    if (authLoading) return;
    const savedTheme = user?.user_metadata?.theme || 'light';
    setThemeMode(savedTheme);
    setLoading(false);
  }, [user, authLoading]);

  const toggleTheme = async (newTheme) => {
    try {
      setThemeMode(newTheme);
      
      // Salvar no Supabase apenas se estiver configurado
      if (supabase) {
        const { error } = await supabase.auth.updateUser({
          data: { theme: newTheme }
        });
      
        if (error) {
          console.error('Erro ao salvar tema no Supabase:', error);
          // Reverter em caso de erro
          setThemeMode(themeMode);
        }
      }
    } catch (error) {
      console.error('Erro ao alterar tema:', error);
    }
  };

  const theme = useMemo(
    () =>
      createTheme(
        themeMode === 'dark' ? darkThemeOptions : lightThemeOptions,
        getMuiLocaleExtension(appLanguage)
      ),
    [themeMode, appLanguage]
  );

  const value = {
    theme,
    themeMode,
    toggleTheme,
    loading,
  };

  if (loading) {
    return <div>Carregando tema...</div>;
  }

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
